import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

type PopperPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end';

interface FormDatePickerProps {
  label: string;
  name: string;
  value?: string;
  onChange?: (value: string, name: string) => void;
  format?: string;
  error?: boolean;
  helperText?: string;
  testId?: string;
  required?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  placement?: PopperPlacement;
  formState?: Record<string, any>;
  setFormState?: React.Dispatch<React.SetStateAction<any>>;
  maxWidth?: string | number;
  sx?: any;
}

const FormDatePicker: React.FC<FormDatePickerProps> = ({
  label,
  name,
  value,
  onChange,
  format = 'MM-DD-YYYY',
  helperText,
  testId = `${name}-date-picker-input`,
  required = false,
  disableFuture = false,
  disablePast = false,
  placement = 'top',
  error = false,
  formState,
  setFormState,
  maxWidth,
  sx = {}
}) => {
  const dateValue = formState ? formState[name] : value;
  const [parsedDate, setParsedDate] = useState<Moment | null>(null);
  
  useEffect(() => {
    // Special handling for YYYY-DD-MM format which might be coming from backend
    if (dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const parts = dateValue.split('-');
      const year = parts[0];
      const possibleDay = parts[1];
      const possibleMonth = parts[2];
      
      if (parseInt(possibleDay) > 12) {
        const correctOrder = `${year}-${possibleMonth}-${possibleDay}`;
        const correctedDate = moment(correctOrder, 'YYYY-MM-DD', true);
        if (correctedDate.isValid()) {
          setParsedDate(correctedDate);
          return;
        }
      }
    }
    
    const newParsedDate = parseMomentDate(dateValue);
    setParsedDate(newParsedDate);
  }, [dateValue, format]);
  
  // Safely parse the date using moment, handling potential invalid dates
  const parseMomentDate = (dateValue: string | undefined): Moment | null => {
    if (!dateValue) return null;
    
    // Check if it looks like a JS Date string (e.g., "Fri Feb 03 1984 00:00:00 GMT+0000")
    if (dateValue.includes('GMT') && /\d{2}:\d{2}:\d{2}/.test(dateValue)) {
      return moment(new Date(dateValue));
    }
    
    // Try to parse with strict mode first for ISO format
    const parsedDate = moment(dateValue, undefined, true);
    if (parsedDate.isValid()) {
      return parsedDate;
    }
    
    // If not valid, try to parse with the specified format
    const parsedWithFormat = moment(dateValue, format, true);
    if (parsedWithFormat.isValid()) {
      return parsedWithFormat;
    }
    
    // If still not valid, try some common formats
    const commonFormats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY/MM/DD'];
    for (const fmt of commonFormats) {
      const parsed = moment(dateValue, fmt, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }
    
    // As a last resort, try direct moment parsing with loose mode
    const looseParsed = moment(dateValue);
    if (looseParsed.isValid()) {
      return looseParsed;
    }
    
    return null;
  };

  const handleDateChange = (date: Moment | null) => {
    // If date is null or invalid, pass empty string
    if (!date || !date.isValid()) {
      const emptyValue = '';
      if (formState && setFormState) {
        setFormState({
          ...formState,
          [name]: emptyValue
        });
      } else if (onChange) {
        onChange(emptyValue, name);
      }
      return;
    }
    
    // Format the valid date according to expected format
    const formattedDate = date.format(format).toString();
    
    if (formState && setFormState) {
      setFormState({
        ...formState,
        [name]: formattedDate
      });
    } else if (onChange) {
      onChange(formattedDate, name);
    }
  };

  const containerStyle = {
    maxWidth: maxWidth || '100%',
    width: '100%',
    ...sx
  };

  return (
    <div style={containerStyle}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          disableFuture={disableFuture}
          disablePast={disablePast}
          label={label}
          value={parsedDate}
          onChange={handleDateChange}
          format={format}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              helperText: helperText,
              error: error,
              required: required,
              InputLabelProps: {
                shrink: true
              },
              inputProps: {
                'data-testid': testId
              },
              sx: {
                width: '100%'
              }
            },
            popper: {
              placement: placement,
              sx: {
                '& .MuiCalendarPicker-root': {
                  width: 'auto'
                }
              }
            }
          }}
        />
      </LocalizationProvider>
    </div>
  );
};

export default FormDatePicker;